import { useEffect, useRef } from "react";

interface SessionTimeoutModalProps {
  secondsLeft: number;
  onKeepAlive: () => void;
  onLogout: () => void;
}

export default function SessionTimeoutModal({
  secondsLeft,
  onKeepAlive,
  onLogout,
}: SessionTimeoutModalProps) {
  const keepAliveRef = useRef(onKeepAlive);
  keepAliveRef.current = onKeepAlive;

  // Auto-focus the "Stay Logged In" button for keyboard accessibility
  const btnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    btnRef.current?.focus();
  }, []);

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const timeStr =
    minutes > 0
      ? `${minutes}:${String(seconds).padStart(2, "0")}`
      : `${seconds}s`;

  // Danger threshold — last 30 seconds
  const isDanger = secondsLeft <= 30;
  // Progress arc (0 → 1 as time runs out)
  const totalWarningSeconds = 120; // matches warningMs default
  const progress = Math.max(0, Math.min(1, 1 - secondsLeft / totalWarningSeconds));
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl w-full max-w-sm overflow-hidden"
        style={{ boxShadow: "0 24px 64px rgba(0,0,0,0.25)" }}
      >
        {/* Top accent bar */}
        <div
          className="h-1 w-full transition-all duration-1000"
          style={{
            background: isDanger
              ? "linear-gradient(90deg, #ef4444, #dc2626)"
              : "linear-gradient(90deg, #f59e0b, #d97706)",
            width: `${(1 - progress) * 100}%`,
          }}
        />

        <div className="p-6 text-center">
          {/* Countdown ring */}
          <div className="flex items-center justify-center mb-4">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 -rotate-90" viewBox="0 0 72 72">
                {/* Track */}
                <circle
                  cx="36"
                  cy="36"
                  r={radius}
                  fill="none"
                  stroke="#f3f4f6"
                  strokeWidth="5"
                />
                {/* Progress */}
                <circle
                  cx="36"
                  cy="36"
                  r={radius}
                  fill="none"
                  stroke={isDanger ? "#ef4444" : "#f59e0b"}
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-1000"
                />
              </svg>
              {/* Center icon + time */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <i
                  className={`ri-time-line text-lg mb-0.5 ${isDanger ? "text-red-500" : "text-amber-500"}`}
                />
                <span
                  className={`text-sm font-bold tabular-nums ${isDanger ? "text-red-600" : "text-amber-600"}`}
                >
                  {timeStr}
                </span>
              </div>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-lg font-bold text-gray-900 mb-1">
            Session Expiring Soon
          </h2>
          <p className="text-sm text-gray-500 mb-5 leading-relaxed">
            You&apos;ve been inactive for a while. For your security, you&apos;ll be
            automatically logged out in{" "}
            <span className={`font-semibold ${isDanger ? "text-red-600" : "text-amber-600"}`}>
              {timeStr}
            </span>
            .
          </p>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onLogout}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                <i className="ri-logout-box-r-line text-sm" />
              </span>
              Logout Now
            </button>
            <button
              ref={btnRef}
              onClick={onKeepAlive}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white transition-all cursor-pointer whitespace-nowrap"
              style={{
                background: isDanger
                  ? "linear-gradient(135deg, #ef4444, #dc2626)"
                  : "linear-gradient(135deg, #10b981, #059669)",
              }}
            >
              <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                <i className="ri-refresh-line text-sm" />
              </span>
              Stay Logged In
            </button>
          </div>

          {/* Security note */}
          <p className="text-xs text-gray-400 mt-4 flex items-center justify-center gap-1">
            <span className="w-3 h-3 inline-flex items-center justify-center">
              <i className="ri-shield-check-line text-xs" />
            </span>
            Auto-logout protects your account when unattended
          </p>
        </div>
      </div>
    </div>
  );
}
